import { Box, Skeleton, Typography, alpha } from '@mui/material';
import dayjs from 'dayjs';
import capitalize from 'lodash.capitalize';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeviceV3 } from '../../../../backendsdk';
import { SubImage } from '../../../../components/SubImage';
import type { ImageMetadata } from '../../../../components/getSubImageDataUrl';
import useApi from '../../../../hooks/api';
import { useImages } from '../../../../hooks/images';
import useIsMobile from '../../../../hooks/isMobile';
import useProfile from '../../../../hooks/profile';
import imageNA from '../../../../images/image_not_available.png';
import palette from '../../../ColorPalette';
import { WELLNESS_TYPE_MAP } from '../../Device/WellnessBox';
import { wellnessColorsByState } from '../../StyledElements';
import { getFFWellnessDetails, getICWellnessDetails } from '../utils';

export const ACTION_ITEMS = [
    'content.wellness.adjust_clean',
    'content.wellness.adjust',
    'content.wellness.remove_blockage_clean',
    'content.wellness.connect_camera',
] as const;
type ActionItem = typeof ACTION_ITEMS[number];
type WellnessType = keyof typeof WELLNESS_TYPE_MAP;
export const WELLNESS_TO_ACTION_MAP: Partial<Record<WellnessType, ActionItem>> = {
    frontfacingwellnessunwellhorizontal: 'content.wellness.adjust_clean',
    frontfacingwellnesscameraverticalhigh: 'content.wellness.adjust_clean',
    frontfacingwellnesscameraverticallow: 'content.wellness.adjust_clean',
    frontfacingwellnesslowqualityinput: 'content.wellness.adjust_clean',
    frontfacingwellnessinsufficientvalidinput: 'content.wellness.adjust_clean',
    faulty: 'content.wellness.adjust',
    'no face': 'content.wellness.adjust',
    no_face: 'content.wellness.adjust',
    peripheral_face: 'content.wellness.adjust',
    blocked: 'content.wellness.remove_blockage_clean',
    disconnected: 'content.wellness.connect_camera',
};

interface WellnessPanelProps {
    device: DeviceV3;
}

const WellnessPanel: React.FC<WellnessPanelProps> = ({ device }: WellnessPanelProps) => {
    const { profile } = useProfile();
    const isMobile = useIsMobile();

    let icWellnessType = 'in_cabin';
    if (
        device.aggregated_wellness &&
        device.aggregated_wellness.in_cabin_wellness &&
        'wellness_state' in device.aggregated_wellness.in_cabin_wellness
    ) {
        icWellnessType = 'in_cabin_v1';
    }

    const icWellnessId = device.aggregated_wellness?.in_cabin_wellness?.id;
    const ffWellnessId = device.aggregated_wellness?.front_facing_wellness?.id;
    const icWellnessDetails = getICWellnessDetails(device, profile.customer.settings.inactive_device_threshold);
    const ffWellnessDetails = getFFWellnessDetails(device, profile.customer.settings.inactive_device_threshold);
    const icWellnessTimestamp = device.aggregated_wellness?.in_cabin_wellness?.timestamp;
    const ffWellnessTimestamp = device.aggregated_wellness?.front_facing_wellness?.timestamp;
    const icActionValue = WELLNESS_TO_ACTION_MAP[icWellnessDetails];
    const ffActionValue = WELLNESS_TO_ACTION_MAP[ffWellnessDetails as keyof typeof WELLNESS_TO_ACTION_MAP];

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 1,
            }}
        >
            {icWellnessId ? (
                <Box sx={{ width: '50%' }}>
                    <WellnessImage
                        wellnessType={icWellnessType}
                        wellnessId={icWellnessId}
                        wellnessStatus={icWellnessDetails}
                        wellnessTimestamp={icWellnessTimestamp}
                        actionValue={icActionValue}
                    />
                </Box>
            ) : null}
            {ffWellnessId ? (
                <Box sx={{ width: '50%' }}>
                    <WellnessImage
                        wellnessType="front_facing"
                        wellnessId={ffWellnessId}
                        wellnessStatus={ffWellnessDetails}
                        wellnessTimestamp={ffWellnessTimestamp}
                        actionValue={ffActionValue}
                    />
                </Box>
            ) : null}
        </Box>
    );
};

interface WellnessImageProps {
    wellnessType: string;
    wellnessId: number;
    wellnessStatus?: string;
    wellnessTimestamp?: number;
    actionValue?: string;
}

export const WellnessImage: React.FC<WellnessImageProps> = (props: WellnessImageProps) => {
    const [isError, setIsError] = useState<boolean>(false);
    const { wellnessType, wellnessId, wellnessStatus, wellnessTimestamp, actionValue } = props;
    const { api } = useApi();
    const getImage = (imageKey: string) => {
        const [wellnessType, wellnessId] = imageKey.split(':', 2);
        return api.apiV2WellnessImageWellnessTypeGet({ wellnessType, wellnessId }, { responseType: 'blob' });
    };
    const [wellnessImages, addToQueue, imageSizes] = useImages(2, getImage, true);
    const { t } = useTranslation();
    const { profile } = useProfile();

    const wellnessKey = `${wellnessType}:${wellnessId}`;

    useEffect(() => {
        addToQueue([wellnessKey]);
    }, [wellnessKey]);

    const imageSize = imageSizes[wellnessKey];

    const wellnessTypeKey = wellnessType.includes('in_cabin') ? 'in_cabin' : 'front_facing';
    let aspectRatio = 4 / 3;
    let position: ImageMetadata | undefined;
    if (imageSize?.width && imageSize?.height) {
        position = [wellnessTypeKey === 'in_cabin' ? 0 : imageSize.width / 2, 0, imageSize.width / 2, imageSize.height];
        if (imageSize.width / imageSize.height === 1504 / 480) {
            if (wellnessTypeKey === 'in_cabin') {
                position = [0, 0, imageSize.height * (4 / 3), imageSize.height];
            } else {
                aspectRatio = 16 / 9;
                position = [
                    imageSize.height * (4 / 3),
                    0,
                    imageSize.width - imageSize.height * (4 / 3),
                    imageSize.height,
                ];
            }
        }
    }

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                width: '100%',
            }}
        >
            {!!wellnessStatus ? (
                <>
                    <Typography sx={{ fontSize: 12, color: 'grayColor.main', fontWeight: 'bold' }}>
                        {t(`content.wellness.${wellnessType}`)}
                    </Typography>
                    <Typography
                        data-testid={`${wellnessTypeKey.replaceAll('_', '-')}-wellness`}
                        sx={{
                            fontSize: 16,
                            fontWeight: 'bold',
                            color: wellnessColorsByState[wellnessStatus as keyof typeof wellnessColorsByState],
                        }}
                    >
                        {capitalize(t(WELLNESS_TYPE_MAP[wellnessStatus as keyof typeof WELLNESS_TYPE_MAP]))}
                    </Typography>
                </>
            ) : null}
            {wellnessImages[wellnessKey] ? (
                <Box style={{ width: '100%', position: 'relative' }}>
                    {isError || wellnessImages[wellnessKey] == imageNA || !position ? (
                        <div style={{ aspectRatio: aspectRatio, width: '100%' }}>
                            <img
                                src={imageNA}
                                style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                            />
                        </div>
                    ) : (
                        <SubImage
                            imageType={wellnessType}
                            spriteUrl={wellnessImages[wellnessKey]}
                            subImageData={position}
                            onError={() => setIsError(true)}
                        />
                    )}
                    {!!wellnessTimestamp && wellnessImages[wellnessKey] !== imageNA ? (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                backgroundColor: alpha(palette.black, 0.3),
                                px: 0.5,
                            }}
                        >
                            <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: palette.white }}>
                                {dayjs.unix(wellnessTimestamp).format(profile.dateTimeFormat)}
                            </Typography>
                        </Box>
                    ) : null}
                </Box>
            ) : (
                <Box style={{ width: '100%', aspectRatio: aspectRatio }}>
                    <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
                </Box>
            )}
            {actionValue !== undefined ? (
                <Typography
                    data-testid={`${wellnessType}-action`}
                    sx={{
                        mt: 0.25,
                        fontSize: 14,
                        fontWeight: 500,
                        color: palette.primary,
                    }}
                >
                    {t(actionValue)}
                </Typography>
            ) : null}
        </Box>
    );
};

export default WellnessPanel;
